import { Component, OnInit, Output, EventEmitter, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subscription} from 'rxjs';
import { CustomerApi } from 'app/shared/api/customer.api';
import { AddCustomer, CustomerList } from 'app/shared/models/customer.models';
import { SharedDataService } from 'app/shared/services/shared-data.service';
import { CompanyInformation } from 'app/shared-html-components/details/models';
import { CompanyData, DAWA, DAWASearch, DawaFunctions } from 'app/shared/utilities/dawa';

@Component({
  selector: 'customer-add',
  templateUrl: './customer-add.component.html',
  styleUrls: ['./customer-add.component.css']
})
export class CustomerAddComponent implements OnInit, OnDestroy {
  @Output() customerId = new EventEmitter<string>();
  @Output('customer') customerDisplay = new EventEmitter<{id:string, name:string}>();

  api = new CustomerApi(this.http);
  dawaApi = new DAWASearch(this.http);
  customer: AddCustomer = new AddCustomer();
  companySubscription: Subscription;
  compInfo:CompanyInformation;
  otherInvoice:boolean = false;
  cvrSearchOutput:any | null = null;
  customerAddressOutput: any[] | null = null;
  invoiceAddressOutput: any | null = null;
  customerAddressFocus:boolean = false;
  invoiceAddressFocus:boolean = false;
  searchIndex:number = 0;
  constructor(private http: HttpClient, private shared: SharedDataService) { }

  ngOnInit(): void {
    this.companySubscription = this.shared.companyInformation().subscribe({
      next:(data:CompanyInformation)=> this.compInfo = data
    })
  }

  ngOnDestroy(): void {
    this.companySubscription.unsubscribe()
  }

  reset(){
    this.customer = new AddCustomer();
    this.cvrSearchOutput = null;
  }

  saveCustomer(){
    this.customer.companyId = this.compInfo.id;
    if(!this.otherInvoice){
      this.copyCustomerDataToInvoice();
    }
    this.api.addCustomer(this.customer).subscribe({
      next:(customerId:string)=> {
        this.customerId.emit(customerId);
        this.customerDisplay.emit({
          id:customerId,
          name: this.customer.customerName
        })
        this.reset();
      },
      error:(err) => console.error("Could not save customer", err)
    })
  }

  copyCustomerDataToInvoice(): void {
    let c: AddCustomer = this.customer;
    c.invoiceName = c.customerName;
    c.invoiceAddress = c.customerAddress;
    c.invoiceZipCode = c.customerZipCode;
    c.invoiceCity = c.customerCity;
    c.invoiceTelephone = c.customerTelephone;
    c.invoiceFax = c.customerFax;
    c.invoiceEmail = c.customerEmail;
    c.invoiceMobile = c.customerMobile;
  }

  cvr(event){
   if(this.compInfo.companyLanguage != 'sv-SE'){
    this.dawaApi.vat(this.customer.cvr, this.compInfo.companyLanguage).subscribe({
      next:(data)=> this.cvrSearchOutput = data
    })
   }
  }

  customerAddress(event: KeyboardEvent) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      const numAddresses = this.customerAddressOutput?.length ?? 0;
      this.updateSearchIndex(numAddresses, event.code === 'ArrowUp' ? 'up' : 'down');
      this.scrollToAddress('customerAdressContainer', this.searchIndex);
    } else if (event.code === 'Enter' && this.searchIndex > -1) {
      this.spreadCustomerAddress(this.customerAddressOutput[this.searchIndex]);
      
    } else if (this.compInfo.companyLanguage === 'da-DK') {
      this.dawaApi.address(this.customer.customerAddress, this.customer.customerZipCode ?? null).subscribe({
        next: (data) => {
          this.customerAddressOutput = data;
        }
      });
    }
  }

  invoiceAddress(event: KeyboardEvent) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      const numAddresses = this.customerAddressOutput?.length ?? 0;
      this.updateSearchIndex(numAddresses, event.code === 'ArrowUp' ? 'up' : 'down');
      this.scrollToAddress('customerAdressContainer', this.searchIndex);
    } else if (event.code === 'Enter' && this.searchIndex > -1) {
      this.spreadInvoiceAddress(this.customerAddressOutput[this.searchIndex]);
    } else if (this.compInfo.companyLanguage === 'da-DK') {
      this.dawaApi.address(this.customer.invoiceAddress, this.customer.invoiceZipCode ?? null).subscribe({
        next: (data) => {
          this.customerAddressOutput = data;
        }
      });
    }
  }

  customerZip(event){
    if (this.compInfo.companyLanguage === 'da-DK' && !!this.customer.customerZipCode) {
      this.dawaApi.cityByZip(this.customer.customerZipCode).subscribe({
        next:(data) => {
          if(data && data.length) {
            this.customer.customerCity = data[0].navn;
            if(!this.customer.customerAddress) document.getElementById('customerAddressAddressInput').focus();
            else document.getElementById('customerAddressEmailInput').focus();
          }
        }
      })
    }
  }

  invoiceZip(event){
    if (this.compInfo.companyLanguage === 'da-DK' && !!this.customer.invoiceZipCode) {
      this.dawaApi.cityByZip(this.customer.invoiceZipCode).subscribe({
        next:(data) => {
          if(data && data.length) {
            this.customer.invoiceCity = data[0].navn;
            if(!this.customer.invoiceAddress) document.getElementById('invoiceAddressAddressInput').focus();
            else document.getElementById('invoiceAddressEmailInput').focus();
          }
        }
      })
    }
  }

  spreadCVRoutput(){
    if(!!this.cvrSearchOutput){
      this.customer = this.addCompanyDataToCustomer(this.cvrSearchOutput, this.customer);
      this.cvrSearchOutput = null;
    }
  }

  spreadCustomerAddress(address:DAWA){
    this.customer.customerAddress = DawaFunctions.getCompleteAddress(address);
    this.customer.customerZipCode = address.adresse.postnr;
    this.customer.customerCity = address.adresse.postnrnavn;
    document.getElementById('customerAddressEmailInput').focus();
  }

  spreadInvoiceAddress(address:DAWA){
    this.customer.invoiceAddress = DawaFunctions.getCompleteAddress(address);
    this.customer.invoiceZipCode = address.adresse.postnr;
    this.customer.invoiceCity = address.adresse.postnrnavn;
    document.getElementById('invoiceAddressEmailInput').focus();
  }

  removeInvoiceAddress(){
    let a = Object.keys(this.customer).filter(x => x.match('invoice'));
    a.forEach(x => this.customer[x] = null)
  }

  addCompanyDataToCustomer(
    companyData: CompanyData,
    addCustomer: AddCustomer
): AddCustomer {
    return {
        ...addCustomer,
        companyId: companyData.vat.toString(),
        cvr: companyData.vat.toString(),
        customerName: companyData.name,
        customerAddress: companyData.address,
        customerZipCode: companyData.zipcode,
        customerCity: companyData.city,
        customerTelephone: companyData.phone ?? '',
        customerEmail: companyData.email ?? '',
    };
}

delayedClose(where: 'customerAddress' | 'invoiceAddress'){
  switch(where) {
    case 'customerAddress' : {
      setTimeout(()=> this.customerAddressFocus = false, 250);
      break
    }
    case 'invoiceAddress':{
      setTimeout(()=> this.invoiceAddressFocus = false, 250);
      break
    }
  }
}

updateSearchIndex(numAddresses: number, direction: 'up' | 'down') {
  if (numAddresses > 0) {
    if (direction === 'up') {
      this.searchIndex = (this.searchIndex - 1 + numAddresses) % numAddresses;
    } else if (direction === 'down') {
      this.searchIndex = (this.searchIndex + 1) % numAddresses;
    }
  }
}

scrollToAddress(containerId: string, addressIndex: number) {
  let container = document.getElementById(containerId);
  if (container) {
    const addressElements = container.querySelectorAll('.singleAddress');
    if (addressElements.length > addressIndex) {
      addressElements[addressIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }
}



}
